import {
  collection,
  collectionGroup,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

// Cache para resultados frecuentes
const searchCache = new Map();
const CACHE_TTL = 10 * 60 * 1000; // 10 minutos

/**
 * Verifica si una colección existe sin lanzar errores
 * @param {string} path - Ruta a la colección
 * @returns {Promise<boolean>} - Si existe o no
 */
const collectionExists = async (path) => {
  try {
    const ref = collection(db, path);
    await getCountFromServer(query(ref, limit(1))); // Eliminamos la variable snapshot no utilizada
    return true;
  } catch (error) {
    console.warn(`Colección no encontrada: ${path}`, error.message);
    return false;
  }
};

/**
 * Resuelve la ruta de una colección con posibles alternativas
 * Si la ruta principal no existe, intenta con alternativas
 */
const resolveCollectionPath = async (basePath, possibleNames, language) => {
  // Lista de posibles nombres alternativos basados en sinónimos comunes
  const alternatives = {
    general: [
      "información",
      "info",
      "general_info",
      "generalinfo",
      "general-info",
      "informacion",
      "general_information",
    ],
    tratamientos: [
      "treatments",
      "tratamiento",
      "medical",
      "terapias",
      "therapies",
      "therapy",
      "terapia",
      "medical_treatments",
    ],
    educacion: [
      "education",
      "educación",
      "learning",
      "aprendizaje",
      "educativo",
      "educational",
      "school",
      "escuela",
    ],
    familia: [
      "family",
      "families",
      "parents",
      "padres",
      "home",
      "hogar",
      "house",
      "casa",
    ],
    specialists: [
      "especialistas",
      "doctors",
      "doctores",
      "médicos",
      "medicos",
      "professionals",
      "profesionales",
      "expertos",
      "experts",
    ],
    resources: [
      "recursos",
      "material",
      "materiales",
      "materials",
      "documents",
      "documentos",
      "files",
      "archivos",
    ],
    entries: [
      "entradas",
      "items",
      "posts",
      "elementos",
      "documentos",
      "documents",
      "content",
      "contenido",
      "data",
      "datos",
    ],
    down_syndrome_info: [
      "down_syndrome",
      "downsyndromeinfo",
      "down-syndrome-info",
      "sindrome_down",
      "sindrome-down",
      "downsyndrome",
      "sindromedown",
      "trisomy21",
      "trisomia21",
    ],
  };

  // Primero intentar la ruta original
  const originalPath = `${basePath}/${language}/${possibleNames[0]}`;
  if (await collectionExists(originalPath)) {
    return originalPath;
  }

  // Luego probar con todas las alternativas
  for (const name of possibleNames) {
    // Alternativas para esta parte específica
    const nameAlternatives = alternatives[name] || [name];

    for (const altName of nameAlternatives) {
      const path = `${basePath}/${language}/${altName}`;
      if (await collectionExists(path)) {
        console.log(
          `🔄 Usando colección alternativa: ${path} en lugar de ${originalPath}`,
        );
        return path;
      }
    }
  }

  // Probar con alternativas para el idioma
  const langAlternatives = {
    es: ["es-ES", "esp", "español", "spanish"],
    en: ["en-US", "eng", "english", "ingles"],
    fr: ["fr-FR", "french", "frances"],
    de: ["de-DE", "german", "aleman"],
  };

  const altLanguages = langAlternatives[language] || [];
  for (const altLang of altLanguages) {
    const path = `${basePath}/${altLang}/${possibleNames[0]}`;
    if (await collectionExists(path)) {
      console.log(
        `🔄 Usando idioma alternativo: ${path} en lugar de ${originalPath}`,
      );
      return path;
    }
  }

  // Intentar colectionGroup como última opción
  console.warn(
    `⚠️ No se encontró colección ${originalPath}, usando fallback genérico`,
  );
  return originalPath; // Devolver la original aunque no exista para mantener consistencia
};

/**
 * Busca en la base de conocimientos información relacionada con un tema
 * Implementación ultra robusta que continúa incluso si hay colecciones faltantes
 */
export const searchKnowledgeBase = async (
  searchQuery,
  language = "es",
  maxResults = 5, // Puedes ajustar esto
  useCache = true,
) => {
  if (
    !searchQuery ||
    typeof searchQuery !== "string" ||
    searchQuery.trim() === ""
  ) {
    return [];
  }

  const normalizedQuery = searchQuery.toLowerCase().trim();
  const cacheKey = `${normalizedQuery}_${language}_${maxResults}`;

  // Verificar caché
  if (useCache && searchCache.has(cacheKey)) {
    const { results, timestamp } = searchCache.get(cacheKey);
    if (Date.now() - timestamp < CACHE_TTL) {
      console.log("✅ Usando resultados en caché para:", normalizedQuery);
      return results;
    }
    searchCache.delete(cacheKey);
  }

  const results = [];
  const processedIds = new Set(); // Para evitar duplicados

  try {
    // Definir posibles colecciones a buscar con prioridades
    const collectionsToSearch = [
      {
        basePath: "blog",
        possibleNames: [],
        isFlat: true,
        priority: 11,
        searchFields: [
          "title",
          "content",
          "excerpt",
          "category",
          "categoryKey",
        ],
        filterFn: (data) => data.status === "published",
      },
      {
        basePath: "specialists_flat",
        possibleNames: ["entries"],
        priority: 10,
        searchFields: [
          "name",
          "specialty",
          "city",
          "country",
          "address",
          "description",
        ],
      },
      {
        basePath: "down_syndrome_info",
        possibleNames: ["general", "articles", "entries", ""],
        priority: 9,
        searchFields: [
          "title",
          "content",
          "contentHTML",
          "summary",
          "description",
          "tags",
          "keywords",
          "category",
        ],
      },
      {
        basePath: "down_syndrome_news",
        possibleNames: [],
        isFlat: true,
        priority: 8,
        searchFields: ["title", "content", "summary", "tags", "category"],
      },
      {
        basePath: `down_syndrome_resources_${language}`,
        possibleNames: [],
        isFlat: true,
        priority: 8,
        searchFields: [
          "name",
          "description",
          "category",
          "location",
          "country",
        ],
      },
    ];

    // Buscar en cada tipo de colección en paralelo (más eficiente)
    // La función resolveCollectionPath se encargará de construir la ruta completa
    // incluyendo el idioma, por ejemplo: `down_syndrome_resources_es`
    const searchPromises = collectionsToSearch.map(
      async ({ basePath, possibleNames, priority, searchFields, isFlat, filterFn }) => {
        try {
          // Para colecciones planas (sin subcolecciones de idioma), usar directamente
          // Para colecciones anidadas, resolver la ruta con alternativas
          const collectionPath = isFlat
            ? basePath
            : await resolveCollectionPath(basePath, possibleNames, language);

          // Si no podemos resolver la colección, intentar con collectionGroup
          let docsRef;
          try {
            docsRef = collection(db, collectionPath);
          } catch (err) {
            if (possibleNames && possibleNames.length > 0 && possibleNames[0]) {
              console.warn(
                `Fallback a collectionGroup para ${basePath}/${possibleNames[0]}`,
              );
              docsRef = collectionGroup(db, possibleNames[0]);
            } else {
              console.warn(`No se pudo acceder a la colección ${collectionPath}`);
              return [];
            }
          }

          // Obtener documentos con manejo de errores
          let snapshot;
          try {
            snapshot = await getDocs(docsRef);
          } catch (err) {
            console.warn(
              `Error obteniendo documentos de ${collectionPath}:`,
              err.message,
            );
            return []; // Continuar con otras colecciones
          }

          // Filtrar documentos que coinciden con la búsqueda
          const matchingDocs = [];

          snapshot.docs.forEach((doc) => {
            try {
              const data = doc.data();
              if (!data) return;

              // Aplicar filtro personalizado si existe (ej: solo publicados)
              if (filterFn && !filterFn(data)) return;

              // Determinar categoría para resultados
              let category = "";
              try {
                category = collectionPath.split("/").pop();
              } catch (e) {
                category = possibleNames[0];
              }

              // Buscar en todos los campos definidos
              let matches = false;

              // Comprobar primero los campos definidos
              for (const field of searchFields) {
                if (
                  data[field] &&
                  String(data[field]).toLowerCase().includes(normalizedQuery)
                ) {
                  matches = true;
                  break;
                }
              }

              // Si no hay coincidencias en campos específicos, buscar en todos los campos
              if (!matches) {
                // Búsqueda profunda en todos los campos de texto
                for (const [, value] of Object.entries(data)) {
                  if (
                    typeof value === "string" &&
                    value.toLowerCase().includes(normalizedQuery)
                  ) {
                    matches = true;
                    break;
                  }
                }
              }

              if (matches) {
                // Crear ID único para evitar duplicados
                const uniqueId = doc.id + collectionPath;

                if (!processedIds.has(uniqueId)) {
                  processedIds.add(uniqueId);

                  // Calcular relevancia para la clasificación posterior
                  let relevance = priority;

                  // Aumentar relevancia si coincide exactamente
                  const titleField = data.title || data.name || "";
                  if (titleField.toLowerCase().includes(normalizedQuery)) {
                    relevance += 5;
                  }

                  matchingDocs.push({
                    id: doc.id,
                    category,
                    _collectionPath: collectionPath, // Para depuración
                    _relevance: relevance,
                    ...data,
                  });
                }
              }
            } catch (docError) {
              console.warn(
                `Error procesando documento ${doc.id}:`,
                docError.message,
              );
              // Continuar con el siguiente documento
            }
          });

          return matchingDocs;
        } catch (collectionError) {
          console.warn(
            `Error completo en colección ${basePath}/${possibleNames[0]}:`,
            collectionError,
          );
          return []; // Continuar con otras colecciones
        }
      },
    );

    // Esperar a que todas las búsquedas terminen
    const searchResults = await Promise.all(searchPromises);

    // Combinar todos los resultados
    const allResults = searchResults.flat();

    // Ordenar por relevancia y limitar
    const sortedResults = allResults.sort((a, b) => {
      return (b._relevance || 0) - (a._relevance || 0);
    });

    // Limpiar propiedades internas y limitar resultados
    const finalResults = sortedResults.slice(0, maxResults).map((item) => {
      // Eliminar propiedades internas
      const { _relevance, _collectionPath, ...cleanItem } = item;
      return cleanItem;
    });

    // Guardar en caché
    if (useCache) {
      searchCache.set(cacheKey, {
        results: finalResults,
        timestamp: Date.now(),
      });
    }

    return finalResults;
  } catch (error) {
    console.error("Error al buscar en la base de conocimientos:", error);
    return results.slice(0, maxResults);
  }
};

/**
 * Obtiene información detallada sobre un tema específico
 * Con múltiples estrategias de recuperación y fallbacks
 */
export const getKnowledgeItem = async (id, category, language = "es") => {
  if (!id) {
    console.warn("Se requiere un ID para obtener el elemento");
    return null;
  }

  // Si no hay categoría, intentar todas
  if (!category) {
    return getKnowledgeItemWithoutCategory(id, language);
  }

  try {
    // Mapeo de categorías a posibles rutas de colección
    const categoryMappings = {
      general: [
        `down_syndrome_info/${language}/general`,
        `down_syndrome_info/${language}/info`,
        `down_syndrome_info/${language}/información`,
        `down_syndrome_info/${language}/informacion`,
      ],
      tratamientos: [
        `down_syndrome_info/${language}/tratamientos`,
        `down_syndrome_info/${language}/treatments`,
        `down_syndrome_info/${language}/tratamiento`,
        `down_syndrome_info/${language}/medical`,
      ],
      educacion: [
        `down_syndrome_info/${language}/educacion`,
        `down_syndrome_info/${language}/educación`,
        `down_syndrome_info/${language}/education`,
        `down_syndrome_info/${language}/learning`,
      ],
      familia: [
        `down_syndrome_info/${language}/familia`,
        `down_syndrome_info/${language}/family`,
        `down_syndrome_info/${language}/parents`,
        `down_syndrome_info/${language}/padres`,
      ],
      specialists: [
        `specialists/${language}/entries`,
        `specialists/${language}/list`,
        `specialists/${language}/items`,
        `specialists/${language}/doctors`,
        `especialistas/${language}/entries`,
      ],
      resources: [
        `resources/${language}/entries`,
        `resources/${language}/list`,
        `resources/${language}/items`,
        `recursos/${language}/entries`,
      ],
    };

    // Normalizar la categoría para buscar en el mapeo
    const normalizedCategory = category.toLowerCase().trim();

    // Encontrar posibles rutas para esta categoría
    let possiblePaths = [];

    // Buscar coincidencias directas primero
    if (categoryMappings[normalizedCategory]) {
      possiblePaths = categoryMappings[normalizedCategory];
    } else {
      // Buscar coincidencias parciales si no hay directas
      for (const [mappingKey, paths] of Object.entries(categoryMappings)) {
        if (
          mappingKey.includes(normalizedCategory) ||
          normalizedCategory.includes(mappingKey)
        ) {
          possiblePaths = [...possiblePaths, ...paths];
        }
      }
    }

    // Si no se encontraron rutas, usar todas las rutas posibles
    if (possiblePaths.length === 0) {
      possiblePaths = Object.values(categoryMappings).flat();
    }

    // Intentar encontrar el documento en todas las rutas posibles
    for (const collectionPath of possiblePaths) {
      try {
        // Primero intentar obtener directamente por ID
        const docRef = doc(db, collectionPath, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            category: normalizedCategory,
            _path: collectionPath, // Para depuración
            ...data,
          };
        }

        // Si no funciona, intentar buscar por campo ID
        const q = query(collection(db, collectionPath), where("id", "==", id));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          return {
            id: snapshot.docs[0].id,
            category: normalizedCategory,
            _path: collectionPath, // Para depuración
            ...docData,
          };
        }
      } catch (pathError) {
        // Continuar con la siguiente ruta
        console.warn(
          `No se encontró ${id} en ${collectionPath}:`,
          pathError.message,
        );
      }
    }

    // Si llegamos aquí, intentar una búsqueda por cualquier campo que contenga el ID
    return await findDocumentByAnyField(id, language);
  } catch (error) {
    console.error("Error al obtener item de conocimiento:", error);
    // Intentar un último método de recuperación
    return await findDocumentByAnyField(id, language);
  }
};

/**
 * Busca un documento cuando no se conoce la categoría
 * Método de último recurso
 */
const getKnowledgeItemWithoutCategory = async (id, language = "es") => {
  try {
    // Colecciones a buscar
    const collections = [
      `down_syndrome_info/${language}/general`,
      `down_syndrome_info/${language}/tratamientos`,
      `down_syndrome_info/${language}/educacion`,
      `down_syndrome_info/${language}/familia`,
      `specialists/${language}/entries`,
      `resources/${language}/entries`,
    ];

    // Intentar encontrar en cada colección
    for (const collectionPath of collections) {
      try {
        const docRef = doc(db, collectionPath, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const category = collectionPath.split("/").pop();
          return {
            id: docSnap.id,
            category,
            ...data,
          };
        }
      } catch (e) {
        // Continuar con la siguiente colección
      }
    }

    // Si no se encuentra, buscar por campo ID
    for (const collectionPath of collections) {
      try {
        const q = query(collection(db, collectionPath), where("id", "==", id));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const category = collectionPath.split("/").pop();
          return {
            id: doc.id,
            category,
            ...doc.data(),
          };
        }
      } catch (e) {
        // Continuar con la siguiente colección
      }
    }

    // Último recurso: buscar en cualquier campo
    return await findDocumentByAnyField(id, language);
  } catch (error) {
    console.error("Error al buscar documento sin categoría:", error);
    return null;
  }
};

/**
 * Método de último recurso para buscar un documento por cualquier campo que coincida
 */
const findDocumentByAnyField = async (value, language = "es") => {
  try {
    // Colecciones a buscar
    const collections = [
      `down_syndrome_info/${language}/general`,
      `down_syndrome_info/${language}/tratamientos`,
      `down_syndrome_info/${language}/educacion`,
      `down_syndrome_info/${language}/familia`,
      `specialists/${language}/entries`,
      `resources/${language}/entries`,
    ];

    const normalizedValue = String(value).toLowerCase();

    // Buscar en todos los documentos si algún campo contiene el valor
    for (const collectionPath of collections) {
      try {
        const snapshot = await getDocs(collection(db, collectionPath));

        for (const doc of snapshot.docs) {
          const data = doc.data();

          // Verificar si algún campo contiene el valor
          for (const [fieldName, fieldValue] of Object.entries(data)) {
            if (
              typeof fieldValue === "string" &&
              fieldValue.toLowerCase().includes(normalizedValue)
            ) {
              const category = collectionPath.split("/").pop();
              return {
                id: doc.id,
                category,
                _foundBy: fieldName, // Para depuración
                ...data,
              };
            }
          }
        }
      } catch (e) {
        // Continuar con la siguiente colección
      }
    }

    console.warn(
      `No se encontró documento que contenga "${value}" en ninguna colección`,
    );
    return null;
  } catch (error) {
    console.error("Error en método de búsqueda de último recurso:", error);
    return null;
  }
};

/**
 * Obtiene las categorías disponibles con manejo robusto
 */
export const getKnowledgeCategories = async (
  language = "es",
  includeStats = false,
) => {
  try {
    // Posibles rutas para categorías
    const possiblePaths = [
      `down_syndrome_info/${language}/categories`,
      `down_syndrome_info/${language}/categorias`,
      `down_syndrome_info/${language}/category`,
      `down_syndrome_info/categories/${language}`,
      `categories/${language}/down_syndrome`,
      `categorias/${language}`,
      "categorías",
    ];

    let categories = [];
    let categoriesFound = false;

    // Intentar obtener categorías de cada ruta posible
    for (const path of possiblePaths) {
      try {
        const categoriesRef = collection(db, path);
        const snapshot = await getDocs(categoriesRef);

        if (!snapshot.empty) {
          categories = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          categoriesFound = true;
          break;
        }
      } catch (e) {
        // Continuar con la siguiente ruta
      }
    }

    // Si no se encuentran categorías, generar categorías predeterminadas
    if (!categoriesFound) {
      console.warn("No se encontraron categorías, usando predeterminadas");

      // Categorías predeterminadas según el idioma
      const defaultCategories =
        language === "en"
          ? [
              { id: "general", name: "General Information", icon: "info" },
              {
                id: "treatments",
                name: "Treatments",
                icon: "medical_services",
              },
              { id: "education", name: "Education", icon: "school" },
              { id: "family", name: "Family Support", icon: "family_restroom" },
              { id: "specialists", name: "Specialists", icon: "person" },
              { id: "resources", name: "Resources", icon: "menu_book" },
            ]
          : [
              { id: "general", name: "Información General", icon: "info" },
              {
                id: "tratamientos",
                name: "Tratamientos",
                icon: "medical_services",
              },
              { id: "educacion", name: "Educación", icon: "school" },
              {
                id: "familia",
                name: "Apoyo Familiar",
                icon: "family_restroom",
              },
              { id: "specialists", name: "Especialistas", icon: "person" },
              { id: "resources", name: "Recursos", icon: "menu_book" },
            ];

      categories = defaultCategories;
    }

    // Obtener estadísticas si se solicitan
    if (includeStats) {
      const statsPromises = categories.map(async (category) => {
        // Mapear categoría a posibles rutas de colección
        const categoryId = category.id.toLowerCase();
        let possibleCollections = [];

        if (
          ["general", "info", "información", "informacion"].includes(categoryId)
        ) {
          possibleCollections = [
            `down_syndrome_info/${language}/general`,
            `down_syndrome_info/${language}/info`,
          ];
        } else if (
          ["tratamientos", "treatments", "medical"].includes(categoryId)
        ) {
          possibleCollections = [
            `down_syndrome_info/${language}/tratamientos`,
            `down_syndrome_info/${language}/treatments`,
          ];
        } else if (
          ["educacion", "educación", "education"].includes(categoryId)
        ) {
          possibleCollections = [
            `down_syndrome_info/${language}/educacion`,
            `down_syndrome_info/${language}/education`,
          ];
        } else if (["familia", "family"].includes(categoryId)) {
          possibleCollections = [
            `down_syndrome_info/${language}/familia`,
            `down_syndrome_info/${language}/family`,
          ];
        } else if (["specialists", "especialistas"].includes(categoryId)) {
          possibleCollections = [
            `specialists/${language}/entries`,
            `especialistas/${language}/entries`,
          ];
        } else if (["resources", "recursos"].includes(categoryId)) {
          possibleCollections = [
            `resources/${language}/entries`,
            `recursos/${language}/entries`,
          ];
        } else {
          possibleCollections = [
            `${categoryId}/${language}/entries`,
            `${categoryId}/${language}`,
          ];
        }

        // Obtener conteo de cada colección posible
        let count = 0;

        for (const collPath of possibleCollections) {
          try {
            const itemsRef = collection(db, collPath);
            const countSnapshot = await getCountFromServer(itemsRef);
            count += countSnapshot.data().count;
            break; // Si no hay error, usar este conteo
          } catch (err) {
            // Continuar con la siguiente ruta
          }
        }

        return {
          ...category,
          itemCount: count,
        };
      });

      return Promise.all(statsPromises);
    }

    return categories;
  } catch (error) {
    console.error("Error al obtener categorías:", error);

    // Devolver categorías predeterminadas en caso de error
    const defaultCategories =
      language === "en"
        ? [
            { id: "general", name: "General Information", icon: "info" },
            { id: "treatments", name: "Treatments", icon: "medical_services" },
            { id: "education", name: "Education", icon: "school" },
            { id: "family", name: "Family Support", icon: "family_restroom" },
          ]
        : [
            { id: "general", name: "Información General", icon: "info" },
            {
              id: "tratamientos",
              name: "Tratamientos",
              icon: "medical_services",
            },
            { id: "educacion", name: "Educación", icon: "school" },
            { id: "familia", name: "Apoyo Familiar", icon: "family_restroom" },
          ];

    return defaultCategories;
  }
};

/**
 * Busca recursos educativos con manejo robusto y paginación
 */
export const searchEducationalResources = async ({
  query = "",
  language = "es",
  pageSize = 8,
  lastVisible = null,
  filters = {}, // {type: [], ageGroup: [], format: []}
  sortBy = "createdAt", // 'createdAt', 'title', 'popularity'
} = {}) => {
  try {
    // Posibles rutas para recursos
    const possiblePaths = [
      `resources/${language}/entries`,
      `recursos/${language}/entries`,
      `resources/${language}/items`,
      `educational_resources/${language}`,
      `educational/${language}`,
      `recursos_educativos/${language}`,
    ];

    let resourcesFound = false;
    let resources = [];
    let finalLastVisible = null;
    let hasMore = false;

    // Intentar cada ruta posible
    for (const collectionPath of possiblePaths) {
      try {
        // Construir consulta base
        const resourcesRef = collection(db, collectionPath);

        // Usar un campo de ordenación que probablemente exista
        let orderByField = "createdAt";

        if (sortBy === "title") {
          orderByField = "title";
        } else if (sortBy === "popularity") {
          orderByField = "views"; // O 'popularity', 'downloads', etc.
        }

        let q = query(resourcesRef, orderBy(orderByField, "desc"));

        // Aplicar filtros si existen y no están vacíos
        if (filters && Object.keys(filters).length > 0) {
          for (const [key, values] of Object.entries(filters)) {
            if (Array.isArray(values) && values.length > 0) {
              try {
                q = query(q, where(key, "in", values));
              } catch (filterError) {
                console.warn(
                  `Error aplicando filtro ${key}:`,
                  filterError.message,
                );
                // Continuar sin este filtro
              }
            }
          }
        }

        // Aplicar paginación
        if (lastVisible) {
          try {
            q = query(q, startAfter(lastVisible), limit(pageSize));
          } catch (paginationError) {
            console.warn(
              "Error aplicando paginación:",
              paginationError.message,
            );
            q = query(q, limit(pageSize));
          }
        } else {
          q = query(q, limit(pageSize));
        }

        // Ejecutar consulta con manejo de errores
        const snapshot = await getDocs(q);

        // Extraer documentos
        resources = snapshot.docs.map((doc) => ({
          id: doc.id,
          _collection: collectionPath, // Para depuración
          ...doc.data(),
        }));

        // Establecer last visible para paginación
        finalLastVisible =
          snapshot.docs.length > 0
            ? snapshot.docs[snapshot.docs.length - 1]
            : null;

        hasMore = snapshot.docs.length === pageSize;
        resourcesFound = true;
        break;
      } catch (pathError) {
        console.warn(
          `Error obteniendo recursos de ${collectionPath}:`,
          pathError.message,
        );
        // Continuar con la siguiente ruta
      }
    }

    // Si no se encontraron recursos y no hay consulta específica, intentar una búsqueda más genérica
    if (!resourcesFound && (!query || query.trim() === "")) {
      try {
        // Intentar con collectionGroup (buscar en cualquier colección que coincida con "resources" o "recursos")
        const resourcesGroup = collectionGroup(db, "entries");
        const groupSnapshot = await getDocs(
          query(resourcesGroup, limit(pageSize)),
        );

        resources = groupSnapshot.docs.map((doc) => ({
          id: doc.id,
          _collection: "collectionGroup/entries", // Para depuración
          ...doc.data(),
        }));

        finalLastVisible =
          groupSnapshot.docs.length > 0
            ? groupSnapshot.docs[groupSnapshot.docs.length - 1]
            : null;

        hasMore = groupSnapshot.docs.length === pageSize;
        resourcesFound = true;
      } catch (groupError) {
        console.warn("Error buscando con collectionGroup:", groupError.message);
      }
    }

    // Filtrar por texto de búsqueda si se proporciona
    if (query && query.trim() !== "") {
      const normalizedQuery = query.toLowerCase().trim();

      resources = resources.filter((resource) => {
        // Buscar en múltiples campos potenciales
        const fieldsToSearch = [
          "title",
          "description",
          "content",
          "summary",
          "tags",
          "keywords",
          "author",
          "category",
        ];

        // También buscar en cualquier campo que contenga "name" o "título"
        const allFields = Object.keys(resource);
        const additionalFields = allFields.filter(
          (field) =>
            field.toLowerCase().includes("name") ||
            field.toLowerCase().includes("title") ||
            field.toLowerCase().includes("titulo") ||
            field.toLowerCase().includes("nombre") ||
            field.toLowerCase().includes("text") ||
            field.toLowerCase().includes("texto") ||
            field.toLowerCase().includes("content") ||
            field.toLowerCase().includes("contenido"),
        );

        const allFieldsToSearch = [
          ...new Set([...fieldsToSearch, ...additionalFields]),
        ];

        // Buscar coincidencias en cualquiera de los campos
        for (const field of allFieldsToSearch) {
          const value = resource[field];

          if (
            typeof value === "string" &&
            value.toLowerCase().includes(normalizedQuery)
          ) {
            return true;
          }

          if (Array.isArray(value)) {
            for (const item of value) {
              if (
                typeof item === "string" &&
                item.toLowerCase().includes(normalizedQuery)
              ) {
                return true;
              }
            }
          }
        }

        return false;
      });
    }

    // Limpiar propiedades internas
    const cleanedResources = resources.map((resource) => {
      const { _collection, ...cleanedResource } = resource;
      return cleanedResource;
    });

    return {
      resources: cleanedResources,
      lastVisible: finalLastVisible,
      hasMore,
    };
  } catch (error) {
    console.error("Error al buscar recursos:", error);
    return { resources: [], lastVisible: null, hasMore: false };
  }
};

/**
 * Limpia la caché de búsquedas
 */
export const clearSearchCache = () => {
  searchCache.clear();
  console.log("Caché de búsquedas limpiado");
};
